import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiGithub,
  FiLinkedin,
  FiTwitter,
  FiArrowRight,
  FiFileText,
} from "react-icons/fi";
import HeroCoder from "../HeroCoder";
import MagneticButton from "../MagneticButton";

const CV_WEB = `${process.env.PUBLIC_URL}/cv.html`;

const NAME = "Shahed Talukder";
const ROLES = [
  "Full-Stack Engineer",
  "Team Lead",
  "IAM & Security Engineer",
  "Problem Solver",
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};
const word = {
  hidden: { opacity: 0, y: 44, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  },
};
const fade = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

function RotatingRole() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ROLES.length), 2800);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="relative inline-flex h-[1.5em] items-center overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          initial={{ y: "0.8em", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-0.8em", opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="whitespace-nowrap text-gradient-anim font-semibold"
        >
          {ROLES[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

const socials = [
  { icon: FiLinkedin, href: "https://www.linkedin.com/in/shahed-talukder/", label: "LinkedIn" },
  { icon: FiGithub, href: "https://github.com/shahed-swe", label: "GitHub" },
  { icon: FiTwitter, href: "https://twitter.com/ShahedTalukder9", label: "Twitter" },
];

const Header = () => {
  return (
    <header
      id="hero"
      className="relative min-h-screen overflow-hidden px-6"
    >
      <div className="container mx-auto grid min-h-screen items-center gap-6 lg:grid-cols-2 lg:gap-10">
        {/* LEFT — text + buttons */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="relative z-10 flex flex-col items-center text-center lg:items-start lg:text-left"
        >
          <motion.span
            variants={fade}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-hairline bg-fill px-4 py-1.5 text-xs font-medium uppercase tracking-[0.28em] text-muted backdrop-blur-md"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_1px_rgba(0,0,0,0.18)]" />
            Available for new projects
          </motion.span>

          <h1 className="text-5xl font-bold leading-[1.04] tracking-tight drop-shadow-[0_10px_30px_rgba(0,0,0,0.08)] sm:text-6xl lg:text-7xl">
            {NAME.split(" ").map((w, idx) => (
              <motion.span
                key={idx}
                variants={word}
                className={`mr-4 inline-block ${
                  idx === 1 ? "text-gradient" : "text-content"
                }`}
              >
                {w}
              </motion.span>
            ))}
          </h1>

          <motion.p
            variants={fade}
            className="mt-6 flex items-center justify-center gap-x-2 text-xl font-light text-muted md:text-2xl lg:justify-start"
          >
            <span>I'm a</span>
            <RotatingRole />
          </motion.p>

          <motion.p
            variants={fade}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted md:text-lg"
          >
            I craft immersive, high-performance web experiences — blending clean
            engineering with motion, depth, and a little bit of magic.
          </motion.p>

          <motion.div
            variants={fade}
            className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap lg:items-start"
          >
            <MagneticButton
              href="#portfolio"
              className="group inline-flex items-center gap-2 rounded-full bg-content px-8 py-3.5 font-semibold text-surface shadow-glow transition-all hover:opacity-90"
            >
              View My Work
              <FiArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </MagneticButton>

            <MagneticButton
              href={CV_WEB}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 rounded-full border border-hairline bg-fill px-8 py-3.5 font-semibold text-content backdrop-blur-md transition-colors hover:bg-fill-2"
            >
              <FiFileText className="transition-transform duration-300 group-hover:-translate-y-0.5" />
              View CV
            </MagneticButton>
          </motion.div>

          <motion.div variants={fade} className="mt-10 flex items-center gap-4">
            {socials.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-hairline bg-fill text-lg text-muted backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-content hover:text-content hover:shadow-glow"
              >
                <Icon />
              </a>
            ))}
          </motion.div>
        </motion.div>

        {/* RIGHT — animated coder illustration */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden h-[40rem] w-full lg:block"
        >
          <HeroCoder />
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-24 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-muted lg:flex"
        aria-label="Scroll to content"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="flex h-9 w-5 justify-center rounded-full border border-hairline p-1">
          <span className="h-1.5 w-1 rounded-full bg-content animate-scroll-down" />
        </span>
      </motion.a>
    </header>
  );
};

export default Header;
